import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  InjectionToken,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastService } from '@techspert-io/user-alerts';
import { Observable } from 'rxjs';
import { MaterialModule } from '../../../../../shared/patterns/material-module';
import { CountryService } from '../../../../../shared/services/country.service';
import { SearchService } from '../../../services/search.service';
import { getTargetMaxForCoreNetwork } from '../../cognisearch-target.constants';
import { ExclusionLabelPipe } from '../../pipes/exclusion-label.pipe';
import { SearchQueryFieldPipe } from '../../pipes/search-query-field.pipe';
import {
  DEFAULT_EXCLUDE_ROLES,
  EXCLUSION_OPTIONS,
  SEARCH_QUERY_CONDITIONS_FIELD_VALUES,
  WORK_SETTINGS_OPTIONS,
} from './search-query-form.constants';
import {
  createSearchJobHistoryConditionForm,
  createSearchQueryConditionForm,
} from './search-query-form.factories';
import {
  ExclusionType,
  IExclusionOption,
  SearchConditionOperator,
  SearchQueryConditionsFields,
  SearchQueryConditionsForm,
  SearchQueryForm,
} from './search-query-form.models';

export type ClipboardWriter = Pick<Clipboard, 'writeText'>;

export const CLIPBOARD = new InjectionToken<ClipboardWriter>('CLIPBOARD', {
  providedIn: 'root',
  factory: () => navigator.clipboard,
});

export const DEFAULT_EXCLUDE_ROLES_TOKEN = new InjectionToken<
  readonly ExclusionType[]
>('DEFAULT_EXCLUDE_ROLES', {
  providedIn: 'root',
  factory: () => DEFAULT_EXCLUDE_ROLES,
});

@Component({
  selector: 'app-search-query-form',
  templateUrl: './search-query-form.component.html',
  styleUrls: ['./search-query-form.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    SearchQueryFieldPipe,
    ExclusionLabelPipe,
  ],
})
export class SearchQueryFormComponent implements OnInit {
  @Input() form: SearchQueryForm;
  @Output() uploadQuery = new EventEmitter();
  @Output() downloadQuery = new EventEmitter();

  private readonly destroyRef = inject(DestroyRef);
  private readonly clipboard = inject(CLIPBOARD);
  readonly excludeRoles = new FormControl<ExclusionType[]>([
    ...inject(DEFAULT_EXCLUDE_ROLES_TOKEN),
  ]);

  readonly pastAdvancedTooltip =
    'Useful for projects where clients require experts limited to past roles active within a certain time, or past roles active before a certain time. For example when a client wants to speak to past ACME employees who have had their NDA expire after 6 months';
  readonly exclusionOptions = [...EXCLUSION_OPTIONS];
  readonly workSettingsOptions = [...WORK_SETTINGS_OPTIONS];

  private isApplyingExcludeRoleSelection = false;
  private lastSyncedExcludeRoleSelection = new Set<ExclusionType>();

  autocompleteOptionMap: Partial<
    Record<
      SearchQueryConditionsFields,
      (searchTerm?: string) => Observable<string[]>
    >
  > = {
    roles: (searchTerm?: string) =>
      this.searchService.autocomplete(searchTerm || '', 'roles'),
    affiliations: (searchTerm?: string) =>
      this.searchService.autocomplete(searchTerm || '', 'affiliations'),
    expertise: (searchTerm?: string) =>
      this.searchService.autocomplete(searchTerm || '', 'expertise'),
  };

  /** Does typing as we can't define types  */
  getAutocompleteOptions(
    field: string
  ): ((searchTerm?: string) => Observable<string[]>) | undefined {
    if (
      SEARCH_QUERY_CONDITIONS_FIELD_VALUES.includes(
        field as SearchQueryConditionsFields
      )
    ) {
      return this.autocompleteOptionMap[field];
    }

    return undefined;
  }

  get countries() {
    return this.countryService.countryList;
  }

  get subdivisions() {
    return this.countryService.getUSStateNames();
  }

  get nameConditions() {
    return this.form.controls.nameConditions;
  }

  get workConditions() {
    return this.form.controls.workConditions;
  }

  get knowledgeConditions() {
    return this.form.controls.knowledgeConditions;
  }

  get jobHistoryConditions() {
    return this.form.controls.jobHistoryConditions;
  }

  get phoneNumberValue() {
    return this.form.controls.phoneNumber.value;
  }

  get targetMax(): number {
    return getTargetMaxForCoreNetwork(this.form.controls.coreNetwork.value);
  }

  constructor(
    private toastService: ToastService,
    private countryService: CountryService,
    private searchService: SearchService
  ) {}

  ngOnInit() {
    this.syncExclusionsToWorkConditions(this.excludeRoles.value || []);
    this.updateTargetValidators();

    this.form.controls.coreNetwork.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateTargetValidators());

    this.excludeRoles.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedExclusions) =>
        this.syncExclusionsToWorkConditions(selectedExclusions || [])
      );

    this.workConditions.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncExcludeRolesFromWorkConditions());
  }

  private updateTargetValidators(): void {
    this.form.controls.target.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(this.targetMax),
    ]);
    this.form.controls.target.updateValueAndValidity();
  }

  getExclusionLabelForTerms(terms: string[]) {
    if (!terms?.length) {
      return;
    }

    const normalizedTerms = new Set(terms.map((t) => this.normalizeTerm(t)));
    const match = this.exclusionOptions.find(
      (option) =>
        option.terms.length === normalizedTerms.size &&
        option.terms.every((t) => normalizedTerms.has(this.normalizeTerm(t)))
    );
    return match?.label;
  }

  togglePhoneNumber() {
    const currentValue = this.form.controls.phoneNumber.value;
    this.form.controls.phoneNumber.patchValue(!currentValue);
  }

  addJobHistoryCondition(operator: SearchConditionOperator) {
    this.jobHistoryConditions.push(
      createSearchJobHistoryConditionForm(operator)
    );
  }

  addCondition(
    controlArray: FormArray,
    field: SearchQueryConditionsFields,
    operator: SearchConditionOperator
  ) {
    controlArray.push(createSearchQueryConditionForm(field, operator));
  }

  removeCondition(controlArray: FormArray, index: number) {
    if (index >= 0 && index < controlArray.length) {
      controlArray.removeAt(index);
    }
  }

  copyCondition(controlArray: FormArray, index: number) {
    if (index >= 0 && index < controlArray.length) {
      const terms = controlArray.value[index].terms.join('| ');

      this.clipboard.writeText(terms).then(() => {
        this.toastService.sendMessage(
          'Successfully copied search terms to clipboard',
          'success'
        );
      });
    }
  }

  private syncExclusionsToWorkConditions(selectedExclusions: ExclusionType[]) {
    const selectedExclusionSet = new Set(selectedExclusions);
    const addedExclusions = this.exclusionOptions.filter(
      ({ value }) =>
        selectedExclusionSet.has(value) &&
        !this.lastSyncedExcludeRoleSelection.has(value)
    );
    const removedExclusions = this.exclusionOptions.filter(
      ({ value }) =>
        this.lastSyncedExcludeRoleSelection.has(value) &&
        !selectedExclusionSet.has(value)
    );

    this.isApplyingExcludeRoleSelection = true;
    try {
      this.removeExclusionTerms(removedExclusions);
      this.addMissingExclusionTerms(addedExclusions);
    } finally {
      this.isApplyingExcludeRoleSelection = false;
      this.syncExcludeRolesFromWorkConditions();
    }
  }

  private syncExcludeRolesFromWorkConditions() {
    if (this.isApplyingExcludeRoleSelection) {
      return;
    }

    const selectedExclusions = this.exclusionOptions
      .filter(({ terms }) => this.hasRoleExclusionTerms(terms))
      .map(({ value }) => value);

    this.lastSyncedExcludeRoleSelection = new Set(selectedExclusions);

    if (
      !this.areExclusionSelectionsEqual(
        this.excludeRoles.value || [],
        selectedExclusions
      )
    ) {
      this.excludeRoles.setValue(selectedExclusions, { emitEvent: false });
    }
  }

  private removeExclusionTerms(exclusions: IExclusionOption[]) {
    const termsToRemove = new Set(
      exclusions.flatMap(({ terms }) =>
        terms.map((term) => this.normalizeTerm(term))
      )
    );

    if (!termsToRemove.size) {
      return;
    }

    for (let i = this.workConditions.length - 1; i >= 0; i--) {
      const condition = this.workConditions.at(i);
      if (!this.isRoleExclusionCondition(condition)) {
        continue;
      }

      const terms = condition.controls.terms.value || [];
      const remainingTerms = terms.filter(
        (term) => !termsToRemove.has(this.normalizeTerm(term))
      );

      if (remainingTerms.length === terms.length) {
        continue;
      }

      if (remainingTerms.length) {
        condition.controls.terms.setValue(remainingTerms);
      } else {
        this.workConditions.removeAt(i);
      }
    }
  }

  private addMissingExclusionTerms(exclusions: IExclusionOption[]) {
    exclusions.forEach(({ terms }) => {
      if (!this.hasRoleExclusionTerms(terms)) {
        this.addExclusionTerms(terms);
      }
    });
  }

  private hasRoleExclusionTerms(terms: string[]): boolean {
    const roleExclusionConditions = this.workConditions.controls.flatMap(
      (condition) =>
        condition.controls.terms.value &&
        this.isRoleExclusionCondition(condition)
          ? condition.controls.terms.value
          : []
    );

    return this.includesExclusionTerms(roleExclusionConditions, terms);
  }

  private isRoleExclusionCondition(
    condition: SearchQueryConditionsForm
  ): boolean {
    return (
      condition.controls.operator.value === 'exclude' &&
      condition.controls.field.value === 'roles'
    );
  }

  private includesExclusionTerms(
    conditionTerms: string[],
    exclusionTerms: string[]
  ): boolean {
    const normalizedConditionTerms = new Set(
      conditionTerms.map((term) => this.normalizeTerm(term))
    );

    return exclusionTerms.every((term) =>
      normalizedConditionTerms.has(this.normalizeTerm(term))
    );
  }

  private normalizeTerm(term: string): string {
    return term.trim().toLowerCase();
  }

  private areExclusionSelectionsEqual(
    current: ExclusionType[],
    next: ExclusionType[]
  ): boolean {
    return (
      current.length === next.length &&
      current.every((value) => next.includes(value))
    );
  }

  private addExclusionTerms(terms: string[]) {
    this.workConditions.push(
      createSearchQueryConditionForm('roles', 'exclude', terms)
    );
  }
}
